import { GameId } from '@bg-shared/enums';
import { differenceInDays } from 'date-fns';
import {
    ICUCondition,
    ICULeaderboard,
    ICUTournament,
    ICUTournamentReward,
} from '../services/CaptainUpAPI';
import {
    IGamificationLeaderboardPlayer,
    IGamificationTournament,
    ITournamentCondition,
    ITournamentReward,
} from '../interfaces';
import { GamificationConditionFilter } from '../enums';

class TournamentsFormatter {
    private readonly MAX_DAYS_TO_SHOW = 7;

    // @visibleForTesting
    private formatRewards(rewards: ICUTournamentReward[]): ITournamentReward[] {
        const grouped = new Map<string, ITournamentReward>();

        rewards.forEach((reward) => {
            const key = `${reward.from_place}-${reward.to_place}`;

            if (!grouped.has(key)) {
                grouped.set(key, {
                    from: reward.from_place,
                    to: reward.to_place,
                    items: [],
                });
            }

            grouped.get(key).items.push({
                amount: reward.reward.amount,
                type: reward.reward.data[0].value,
            });
        });

        return Array.from(grouped.values());
    }

    private formatConditions(conditions: ICUCondition[]): ITournamentCondition[] {
        const grouped = new Map<string, ITournamentCondition>();

        conditions.forEach((condition) => {
            condition.filters
                .filter((filter) => filter.field !== GamificationConditionFilter.GAMES_IDS)
                .forEach((filter) => {
                    if (!grouped.has(filter.field)) {
                        grouped.set(filter.field, {
                            filter: filter.field,
                            operator: filter.operator,
                            values: [],
                        });
                    }

                    const group = grouped.get(filter.field);

                    filter.values[0]?.forEach((value) => {
                        if (!group.values.includes(value)) {
                            group.values.push(value);
                        }
                    });
                });
        });

        // To avoid human error on CaptainUp side BO configuration
        return Array.from(grouped.values()).filter((condition) => !!condition.values.length);
    }

    // @visibleForTesting
    private extractGames(conditions: ICUCondition[] | null): GameId[] {
        return (
            conditions?.reduce<GameId[]>((result, condition) => {
                condition.filters
                    .filter((filter) => filter.field === GamificationConditionFilter.GAMES_IDS)
                    .forEach((filter) => {
                        filter.values[0].forEach((value) => {
                            const gameId = Number(value);
                            if (!result.includes(gameId)) {
                                result.push(gameId);
                            }
                        });
                    });
                return result;
            }, []) ?? []
        );
    }

    public formatTournaments(tournaments: ICUTournament[]): IGamificationTournament[] {
        return tournaments
            .map((tournament) => ({
                id: tournament.id,
                active: tournament.active,
                activeFromDate: tournament.active_from_date,
                conditions: this.formatConditions(tournament.conditions),
                conditionsOperator: tournament.conditions_operator,
                description: tournament.description,
                disabledFromDate: tournament.disabled_from_date,
                name: tournament.name,
                image: tournament.preset_image,
                rewards: this.formatRewards(tournament.tournament_rewards ?? []),
                custom: tournament.custom,
                gamesIds: this.extractGames(tournament.conditions),
            }))
            .filter(
                (tournament) =>
                    tournament.active &&
                    !!tournament.conditions?.length &&
                    differenceInDays(new Date(), new Date(tournament.disabledFromDate)) <=
                        this.MAX_DAYS_TO_SHOW,
            );
    }

    public formatLeaderboards(leaderboards: ICULeaderboard[]): IGamificationLeaderboardPlayer[] {
        return leaderboards.map((player) => ({
            id: player.id,
            image: player.image,
            name: player.name,
            level: player.level,
            score: player.requested_leaderboard_score,
            position: player.requested_leaderboard_position,
        }));
    }
}

export const tournamentsFormatter = new TournamentsFormatter();
